import AccessTokenStore from '../accessTokenStore'
import TokenRefresher from '../tokenRefresher'
import URLBuilder from '../URLBuilder'

import chooseRecovery from './chooseRecovery'
import isSessionOver from './isSessionOver'

import {
  ABANDON,
  PASS_THROUGH,
  REFRESH,
  RETRY_FLAG,
} from './constants'


/**
 * Builds a response interceptor that recovers from a single expired token:
 * refresh once, replay the original request, and give up if that still fails.
 *
 * What each failure has earned is `chooseRecovery()`'s decision; this carries
 * it out.
 *
 * Takes the instance it is registered on so the replay runs through the same
 * request interceptors, which is what re-attaches the replacement token.
 * Passing it in also avoids importing the instance that imports this.
 *
 * @param {Object} client - the Axios instance to replay through
 *
 * @returns {Function} an Axios response-rejection handler
 */
export default function makeRefreshOn401Interceptor(client) {
  return (error) => {
    const {
      config,
      response,
    } = error

    const recovery = chooseRecovery({
      config,
      heldToken: AccessTokenStore.getAccessToken(),
      response,
    })

    if ( recovery === PASS_THROUGH ) {
      return Promise.reject(error)
    }

    if ( recovery === ABANDON ) {
      abandonSession()

      return Promise.reject(error)
    }

    if ( recovery !== REFRESH ) {
      return replay(client, config)
    }

    // Every request that failed together shares the one refresh, so a failure
    // runs this for each of them and the session is abandoned once per failed
    // request rather than once overall. The repeated navigation resolves to the
    // same URL, so the duplicates cost nothing.
    //
    // A 401 from the refresh is taken at face value here, as a dead credential
    // rather than two tabs racing for one. `TokenRefresher` serializes
    // refreshes across tabs, so a tab that waits its turn spends the cookie its
    // predecessor rotated rather than the one that predecessor consumed.
    return TokenRefresher.refresh()
      .catch((refreshError) => {
        if ( isSessionOver(refreshError) ) {
          abandonSession()
        }

        throw refreshError
      })
      .then(() => {
        return replay(client, config)
      })
  }
}

// The flag rides on the config so that the replay is recognisable when it comes
// back, and the spread keeps everything else the request was sent with,
// including its body.
//
function replay(client, config) {
  return client({
    ...config,
    [RETRY_FLAG]: true,
  })
}

// Navigates for real rather than routing, so that the reload re-establishes
// error reporting, feature flags and the user trackers from scratch.
//
function abandonSession() {
  AccessTokenStore.clear()

  window.location.assign(buildLoginURL())
}

function buildLoginURL() {
  const returnPath = `${ window.location.pathname }${ window.location.search }`

  return `${ URLBuilder.loginURL() }?redirect=${ encodeURIComponent(returnPath) }`
}
